import { InboxOutlined } from '@ant-design/icons';
import { Input, Modal, Select } from 'antd';
import Dragger from 'antd/lib/upload/Dragger';
import { UploadFile } from 'antd/lib/upload/interface';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useGlobalContext } from '../../../context/GlobalContext';
import { IContaDTO, IDespesaFullDTO, IDespesaPagamentoDTO, ITipoPagamentoDTO } from '../../../models/happy-code-api.model';
import { Predicate } from '../../../models/predicate.model';
import { useContaService } from '../../../services/conta.service';
import { useDespesaService } from '../../../services/despesa.service';
import { useNotificationService } from '../../../services/notification.service';
import { useTipoPagamentoService } from '../../../services/tipo-pagamento.service';
import { toMoment } from '../../../util/format';
import { disabledDateAfterToday } from '../../../util/util';
import useUploadFile from '../CadastroDespesa/hooks/use-file';
import { UploadFileWithDesc } from '../CadastroDespesa/types/file-interface';
import BaseDatePicker from './base-date-picker';
import BaseInput from './base-input';
import BaseSelect from './base-select';
import Styles from './components.module.scss';

interface ModalConfirmarPagamentoProps {
	open: boolean;
	setOpen: Dispatch<SetStateAction<boolean>>;
	despesa: IDespesaFullDTO;
	refreshTable: () => void;
}

export default function ModalConfirmarPagamento({ open, setOpen, despesa, refreshTable }: ModalConfirmarPagamentoProps) {
	const [despesaToUpdate, setDespesaToUpdate] = useState<IDespesaPagamentoDTO | null>();
	const [tipoPagamentoList, setTipoPagamentoList] = useState<ITipoPagamentoDTO[]>();
	const [contaList, setContaList] = useState<IContaDTO[]>();
	const { unidade } = useGlobalContext();
	const { fileList, onChangeUpload, onDelete, updateDescription, syncUploadsPayment } = useUploadFile(despesaToUpdate);

	const despesaService = useDespesaService();
	const tipoPagamentoService = useTipoPagamentoService();
	const contaService = useContaService();
	const notification = useNotificationService();
	const { setIsGlobalLoading } = useGlobalContext();

	const patchDespesa = async () => {
		despesaToUpdate.id = despesa.id;
		await syncUploadsPayment();
		setIsGlobalLoading(true);
		despesaService
			.patchPaga(despesaToUpdate)
			.then(() => {
				notification({ description: 'Despesa paga com sucesso!', type: 'success', message: 'Sucesso!' });
				refreshTable();
			})
			.finally(() => {
				setIsGlobalLoading(false);
			});
	};
	const validateFields = () => {
		if (!despesaToUpdate) {
			notification({
				description: 'Preencha os campos.',
				type: 'warning',
				message: 'Aviso!',
			});
			return false;
		}
		if (!despesaToUpdate.dhPagamento) {
			notification({
				description: 'Preencha a data de pagamento.',
				type: 'warning',
				message: 'Aviso!',
			});
			return false;
		}
		if (!despesaToUpdate.vlPago) {
			notification({
				description: 'Preencha o valor pago.',
				type: 'warning',
				message: 'Aviso!',
			});
			return false;
		}
		if (!despesaToUpdate.tipoPagamentoId) {
			notification({
				description: 'Preencha o tipo de pagamento.',
				type: 'warning',
				message: 'Aviso!',
			});
			return false;
		}
		if (despesaToUpdate.vlPago > despesa.vlDespesa) {
			notification({
				description: 'O valor pago não pode ser maior que o valor da despesa.',
				type: 'warning',
				message: 'Aviso!',
			});
			return false;
		}
		return true;
	};
	const handleOk = async () => {
		if (!validateFields()) return;
		await patchDespesa();
		setOpen(false);
	};

	const findContas = async () => {
		const predicate = new Predicate();
		predicate.addOption('unidade.id', unidade?.id);
		contaService.findList(predicate).then(({ data }) => setContaList(data));
	};
	const findTipoPagamento = async () => {
		const predicate = new Predicate();
		predicate.addOption('ativo', true);
		tipoPagamentoService.findList(predicate).then(({ data }) => setTipoPagamentoList(data));
	};
	useEffect(() => {
		setIsGlobalLoading(true);
		Promise.all([findContas(), findTipoPagamento()]).finally(() => setIsGlobalLoading(false));
	}, []);
	return (
		<Modal
			destroyOnClose
			afterClose={() => {
				setDespesaToUpdate(null);
			}}
			style={{ top: 20 }}
			title="Confirmar Pagamento"
			visible={open}
			onOk={handleOk}
			onCancel={() => setOpen(false)}
			okText="Confirmar Pagamento"
			cancelText="Cancelar"
		>
			<div className={Styles.modalContainer}>
				<BaseInput
					min={0}
					value={despesa?.vlDespesa}
					disabled
					type="number"
					labelConfig={{ label: 'Valor da despesa', tooltip: true, tooltipText: 'Valor da despesa cadastrada.' }}
					width={'60%'}
					placeholder="R$ 0,00"
				/>
				<BaseInput
					value={despesaToUpdate?.vlPago}
					min={0}
					max={despesa?.vlDespesa}
					onChange={(e) => setDespesaToUpdate({ ...despesaToUpdate, vlPago: Number(e.target.value) })}
					type="number"
					labelConfig={{ label: 'Valor pago', required: true, tooltip: true, tooltipText: 'Valor efetivamente pago.' }}
					width={'60%'}
					placeholder="R$ 0,00"
				/>
				<BaseSelect
					value={despesaToUpdate?.tipoPagamentoId}
					onChange={(item) => setDespesaToUpdate({ ...despesaToUpdate, tipoPagamentoId: item })}
					labelConfig={{ label: 'Tipo de pagamento', required: true, tooltip: true, tooltipText: 'Selecione o tipo (Pix, boleto, cartão etc.).' }}
					placeholder="Selecione o tipo"
					width={'60%'}
				>
					{tipoPagamentoList?.map((item) => (
						<Select.Option key={item.id} value={item.id}>
							{item.descricao}
						</Select.Option>
					))}
				</BaseSelect>
				<BaseSelect
					value={despesaToUpdate?.contaId}
					onChange={(item) => setDespesaToUpdate({ ...despesaToUpdate, contaId: item })}
					labelConfig={{
						label: 'Conta',
						tooltip: true,
						tooltipText: 'Contas cadastradas no sistema.',
					}}
					placeholder="Selecione"
					width={'60%'}
				>
					{contaList?.map((item) => (
						<Select.Option key={item.id} value={item.id}>
							{item.descricao}
						</Select.Option>
					))}
				</BaseSelect>
				<BaseDatePicker
					value={toMoment(despesaToUpdate?.dhPagamento)}
					onChange={(item) => setDespesaToUpdate({ ...despesaToUpdate, dhPagamento: item.toDate() })}
					labelConfig={{ label: 'Data de pagamento', required: true, tooltip: true, tooltipText: 'Data em que o pagamento foi realizado.' }}
					placeholder="Selecione uma data"
					width={'60%'}
					disabledDate={disabledDateAfterToday}
				/>
				<BaseInput
					value={despesaToUpdate?.observacao}
					onChange={(e) => setDespesaToUpdate({ ...despesaToUpdate, observacao: e.target.value })}
					labelConfig={{ label: 'Observação' }}
					width={'60%'}
					placeholder="Observação"
				/>
				<BaseInput
					value={despesaToUpdate?.vlJuros}
					onChange={(e) => setDespesaToUpdate({ ...despesaToUpdate, vlJuros: Number(e.target.value) })}
					type="number"
					labelConfig={{ label: 'Juros' }}
					width={'60%'}
					placeholder="R$ 0,00"
				/>
				<BaseInput
					value={despesaToUpdate?.vlMulta}
					onChange={(e) => setDespesaToUpdate({ ...despesaToUpdate, vlMulta: Number(e.target.value) })}
					type="number"
					labelConfig={{ label: 'Multa' }}
					width={'60%'}
					placeholder="R$ 0,00"
				/>
				<BaseInput
					value={despesaToUpdate?.vlDesconto}
					onChange={(e) => setDespesaToUpdate({ ...despesaToUpdate, vlDesconto: Number(e.target.value) })}
					type="number"
					labelConfig={{ label: 'Desconto' }}
					width={'60%'}
					placeholder="R$ 0,00"
				/>
				<div style={{ width: '70%', marginTop: '1rem' }}>
					<p>
						<strong>Comprovante de Pagamento</strong>
					</p>
					<Dragger
						listType="text"
						multiple
						customRequest={({ file, onSuccess }) => {
							setTimeout(() => onSuccess?.('ok'), 0);
						}}
						onChange={onChangeUpload}
						onRemove={onDelete}
						fileList={fileList as UploadFile[]}
						itemRender={(originNode, file, files, { remove }) => (
							<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
								<div
									style={{
										minWidth: 200,
										maxWidth: 200,
										overflow: 'hidden',
										textOverflow: 'ellipsis',
										whiteSpace: 'nowrap',
									}}
								>
									{originNode}
								</div>
								<div style={{ flex: 1, padding: 10 }}>
									<Input
										placeholder="Descrição do arquivo"
										maxLength={100}
										value={(file as UploadFileWithDesc).description}
										onChange={(e) => updateDescription(file.uid, e.target.value)}
									/>
								</div>
								{/* <a onClick={() => remove?.(file)}>Excluir</a> */}
							</div>
						)}
					>
						<p className="ant-upload-drag-icon">
							<InboxOutlined rev={undefined} />
						</p>
						<p className="ant-upload-text">Clique ou arraste o arquivo para esta área</p>
						<p className="ant-upload-hint">Tamanho máximo do arquivo suportado: 20MB</p>
					</Dragger>
				</div>
			</div>
		</Modal>
	);
}
